import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { Button, Badge } from "react-bootstrap";
import { fetchExamCategories, deleteExamCategory } from "../../redux/slices/examCategorySlice";
import ExamCategoryModal from "./ExamCategoryModal";
import ConfirmModal from "../Common/ConfirmModal";
import InfoPill from "../Common/InfoPill";
import { FaPencilAlt, FaRegTrashAlt, FaImage } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const SafeImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  return hasError || !src ? (
    <div
      style={{
        width: "45px",
        height: "45px",
        borderRadius: "4px",
        backgroundColor: "#f8f9fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #dee2e6"
      }}
    >
      <i className="fa-solid fa-graduation-cap text-secondary" style={{ fontSize: "1.2rem" }}></i>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      style={{
        width: "45px",
        height: "45px",
        objectFit: "cover",
        borderRadius: "4px",
        border: "1px solid #dee2e6"
      }}
      onError={() => setHasError(true)}
    />
  );
};

const TABLE_HEADERS = [
  {
    prop: "image_url",
    title: "Image",
    cell: (row) => <SafeImage src={row.image_url} alt={row.name} />,
  },
  { prop: "name", title: "Category Name", isSortable: true, isFilterable: true },
  { 
    prop: "image_url", 
    title: "Image", 
    cell: (row) => <SafeImage src={row.image_url} alt={row.name} /> 
  },
  { prop: "description", title: "Description", cell: (row) => row.description || "-" },
  { 
    prop: "status", 
    title: "Status", 
    isSortable: true, 
    cell: (row) => <Badge bg={row.status === "Active" ? "success" : "danger"}>{row.status}</Badge> 
  },
  { 
    prop: "createddate", 
    title: "Created Date", 
    isSortable: true, 
    cell: (row) => formatDateToDDMMYYYY(row.createddate) 
  },
  {
    prop: "actions",
    title: "Actions",
    cell: (row) => (
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-dark" onClick={(e) => { e.stopPropagation(); }}><FaPencilAlt /></Button>
        <Button size="sm" variant="outline-danger"><FaRegTrashAlt /></Button>
      </div>
    ),
  },
];

const ExamCategories = () => {
  const dispatch = useDispatch();
  const { examCategories, isLoading } = useSelector((state) => state.examCategories);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchExamCategories());
  }, [dispatch]);

  const handleEdit = (id) => {
    setEditingId(id);
    setShowModal(true);
  };

  const handleDelete = (id, name) => {
    setItemToDelete({ id, name });
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await dispatch(deleteExamCategory(itemToDelete.id)).unwrap();
        toast.success(`Category "${itemToDelete.name}" deleted`);
        setShowConfirmModal(false);
      } catch (error) {
        toast.error(error || "Delete failed");
      }
    }
  };

  // Re-define actions cell inside to access handleEdit/handleDelete
  const tableHeaders = React.useMemo(() => TABLE_HEADERS.map(h => {
    if (h.prop === "actions") {
      return {
        ...h,
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-dark" onClick={() => handleEdit(row.id)}><FaPencilAlt /></Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(row.id, row.name)}><FaRegTrashAlt /></Button>
          </div>
        )
      };
    }
    return h;
  }), []);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading exam categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ExamCategoryModal show={showModal} handleClose={() => { setShowModal(false); setEditingId(null); }} editId={editingId} />
      <ConfirmModal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)} 
        onConfirm={confirmDelete} 
        title="Delete Exam Category" 
        message={`Are you sure you want to delete "${itemToDelete?.name}"?`}
        variant="danger"
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i className="fa-solid fa-chevron-right" style={{ marginLeft: "1px", marginRight: "1px" }}></i>{" "}
          Exam Categories
        </Link>
      </div>

      <DatatableWrapper 
        body={examCategories || []} 
        headers={tableHeaders || []} 
        paginationOptionsProps={{ initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] } }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
              <Filter placeholder="Search categories..." className="w-100" />
            </div>
            <PaginationOptions />
            <InfoPill left="Total Categories" right={examCategories?.length || 0} />
          </div>

          <div className="d-flex align-items-center">
            <Button variant="dark" className="btn-sm" onClick={() => setShowModal(true)}>
              New Category
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default ExamCategories;
